import { defineStore } from "pinia";
import { ref } from "vue";
import { SpeckleMaterial } from "../../../types/Views/MappingWizard/SpeckleMaterial";
import { BuildingProjectType } from "@/features/BuildingProject";
import { initializeBranchMaterials } from "../../../services/Views/MappingWizard/InitializeBranchMaterials";
import { RecommendationOverview } from "../../../types/Views/MappingWizard/RecommendationResponse";
import { inspectStore } from "../../Utils/Inspect/InspectStore";

// DataProviders
import { useTransportInventory } from "@/dataProviders/TransportInventory";

// API
import { getDashboardSummary } from "@/api/services/Dashboard";
import calculationTypeVariant from "@/services/enums/calculationTypeVariant";
import { useProjectWrapper } from "@/dataProviders/ProjectWrapper";

export const newMappingStore = defineStore("newMappingStore", () => {
  // ///////////////////
  // Providers
  // ///////////////////

  const transportInventory = useTransportInventory();

  // ///////////////////
  // Refs
  // ///////////////////

  const currentProject = ref<BuildingProjectType>();
  const currentBranchMaterials = ref<Array<SpeckleMaterial>>();
  const allBranches = ref<{ [key: string]: Array<SpeckleMaterial> }>();
  const byType = ref<boolean>(false);
  const currentBranch = ref<string>();
  const allBranchNames = ref<Array<string>>([]);
  const currentInspectingMaterial = ref<SpeckleMaterial>();
  const currentInspectingMaterialsParent = ref<SpeckleMaterial>();
  const forceUpdateInteger = ref<number>(0);
  const forceUpdateInspectInteger = ref<number>(0);
  const lifeTimeUpdater = ref<number>(0);
  const co2Budget = ref<number>(0);
  const co2BudgetUnit = ref<{ label: string; id?: string }>({
    label: "kgCO₂-eq/m²/year",
  });
  const mappingPercentage = ref<number>(0);
  const isUpdatingCO2Budget = ref<boolean>(false);
  const specialConditionTypes = ref<unknown[]>([]);
  const projectWrapper = useProjectWrapper();
  const dashboardSummaryIsLoading = ref<boolean>(true);

  // ///////////////////
  // Getters
  // ///////////////////

  const getCo2Budget = () => {
    return co2Budget.value;
  };

  const getCo2BudgetUnit = () => {
    return co2BudgetUnit.value;
  };

  const getMappingPercentage = () => {
    return mappingPercentage.value;
  };

  const getCurrentProject = () => {
    return currentProject.value;
  };

  const getAllBranches = () => {
    return allBranches.value;
  };

  const getCurrentBranch = () => {
    return currentBranch.value;
  };

  const getAllBranchNames = () => {
    return allBranchNames.value;
  };

  const getByType = () => {
    return byType.value;
  };

  const getCurrentInspectingMaterial = () => {
    return currentInspectingMaterial.value;
  };

  const getCurrentInspectingMaterialsParent = () => {
    return currentInspectingMaterialsParent.value;
  };

  const getLifeTimeUpdater = () => {
    return lifeTimeUpdater.value;
  };

  const getForceUpdateInspectInteger = () => {
    return forceUpdateInspectInteger.value;
  };

  const getCurrentBranchMaterials = (): Array<SpeckleMaterial> => {
    return currentBranchMaterials.value ?? [];
  };

  // ///////////////////
  // Setters
  // //////////////////

  const setCurrentProject = (newProject: BuildingProjectType) => {
    currentProject.value = newProject;
  };

  const setAllBranches = (
    newBranches: { [key: string]: Array<SpeckleMaterial> },
    recommendations?: { [key: string]: RecommendationOverview }
  ) => {
    if (recommendations) {
      Object.entries(newBranches).forEach(([_branchName, branchMaterials]) => {
        branchMaterials.forEach((branchMaterial) => {
          branchMaterial.children?.forEach((speckleChild) => {
            Object.entries(recommendations).forEach(([speckleMaterialId, recommendation]) => {
              if (speckleMaterialId === speckleChild.id) {
                speckleChild.recommendations = recommendation.recommendations;
              }
            });
          });
        });
      });
    }
    allBranches.value = newBranches;
  };

  const setCurrentBranch = (newBranch: string) => {
    currentBranch.value = newBranch;
  };

  const setAllBranchNames = (newValue: Array<string>) => {
    allBranchNames.value = newValue;
  };

  const setCurrentBranchMaterials = async (newMaterials: Array<SpeckleMaterial>, autoMap?: boolean) => {
    if (autoMap) {
      currentBranchMaterials.value = newMaterials;
      return;
    }
    currentBranchMaterials.value = initializeBranchMaterials(newMaterials, byType.value);
  };

  const setByType = (newByType: boolean) => {
    byType.value = newByType;
  };

  const setCurrentInspectingMaterial = (newMaterial: SpeckleMaterial) => {
    inspectStore().addInspection(newMaterial);
    currentInspectingMaterial.value = newMaterial;
  };

  const setCurrentInspectingMaterialsParent = (newMaterial: SpeckleMaterial) => {
    currentInspectingMaterialsParent.value = newMaterial;
  };

  const setCurrentInspectingMaterialNewLifetime = (value: number) => {
    if (currentInspectingMaterial.value === undefined) return;
    currentInspectingMaterial.value.newLifeTime = value;
    lifeTimeUpdater.value++;
  };

  // ///////////////////
  // Methods
  // ///////////////////

  const updateInspectingMaterial = () => {
    currentBranchMaterials.value?.forEach((speckleGroup) => {
      const found = speckleGroup.children?.find((child) => child.id === currentInspectingMaterial.value?.id);
      if (found) {
        currentInspectingMaterial.value = found;
        return;
      }
    });
    forceUpdateInspectInteger.value++;
  };

  const increaseLifeTimeUpdater = () => {
    lifeTimeUpdater.value++;
  };

  const stopInspectingMaterial = () => {
    currentInspectingMaterial.value = undefined;
  };

  const unloadBuildingProject = () => {
    currentProject.value = undefined;
    currentBranchMaterials.value = undefined;
    allBranches.value = undefined;
    byType.value = false;
    currentBranch.value = undefined;
    currentInspectingMaterial.value = undefined;
    forceUpdateInteger.value = 0;
  };

  const unloadBranchMaterials = () => {
    currentBranchMaterials.value = undefined;
    allBranches.value = undefined;
    currentBranch.value = undefined;
  };

  const handleUpdateCO2Budget = async () => {
    // Prevent concurrent calls
    if (isUpdatingCO2Budget.value) {
      return;
    }
    isUpdatingCO2Budget.value = true;
    if (!currentProject.value || !currentBranch.value) {
      isUpdatingCO2Budget.value = false;
      return;
    }

    // Fetch dashboard data
    dashboardSummaryIsLoading.value = true;
    const data = await getDashboardSummary(currentProject.value.id, {
      models: [currentBranch.value],
    });
    dashboardSummaryIsLoading.value = false;

    const emissionBudget = data.emissionBudget;

    const specialConditionsEnabled = projectWrapper.getProjectWrapper().project.specialConditionConfig.enabled || false;

    let usedEmission;
    if (specialConditionsEnabled && emissionBudget.calculationType.variant?.variant === calculationTypeVariant.BR182025) {
      usedEmission = emissionBudget.used.children.SpecialCondition;
    } else {
      usedEmission = emissionBudget.used;
    }

    co2Budget.value = usedEmission.value ?? 0;
    co2BudgetUnit.value = data.unit ?? { label: "kgCO₂-eq/m²/year" };
    mappingPercentage.value = usedEmission.percentage;

    isUpdatingCO2Budget.value = false;

    // Update transport inventory
    await transportInventory.fetchInventory(currentProject.value.id);
  };

  const removeDeletedChild = (groupId: string, childId: string) => {
    if (!currentBranchMaterials.value) return;

    const childIndex = currentBranchMaterials.value
      .find(({ id }) => id === groupId)
      ?.children?.findIndex(({ id }) => id === childId);

    if (childIndex === undefined) return;

    const foundGroupWithChildren = currentBranchMaterials.value.find(({ id }) => id === groupId)?.children;

    foundGroupWithChildren?.splice(childIndex, 1);
    setCurrentBranchMaterials(currentBranchMaterials.value);
  };

  const reset = () => {
    currentProject.value = undefined;
    currentBranchMaterials.value = undefined;
    allBranches.value = undefined;
    byType.value = false;
    currentBranch.value = undefined;
    currentInspectingMaterial.value = undefined;
    currentInspectingMaterialsParent.value = undefined;
    forceUpdateInteger.value = 0;
    forceUpdateInspectInteger.value = 0;
    lifeTimeUpdater.value = 0;
    co2Budget.value = 0;
    co2BudgetUnit.value = { label: "kgCO₂-eq/m²/year" };
    mappingPercentage.value = 0;
    isUpdatingCO2Budget.value = false;
  };

  return {
    // Refs
    dashboardSummaryIsLoading,
    specialConditionTypes,

    // Getters
    getCurrentProject,
    getByType,
    getCo2Budget,
    getCo2BudgetUnit,
    getCurrentBranch,
    getCurrentBranchMaterials,
    getAllBranches,
    getCurrentInspectingMaterial,
    getMappingPercentage,
    getCurrentInspectingMaterialsParent,
    getLifeTimeUpdater,
    getForceUpdateInspectInteger,
    getAllBranchNames,

    // Setters
    setCurrentProject,
    setByType,
    setCurrentBranch,
    setCurrentBranchMaterials,
    setAllBranches,
    setCurrentInspectingMaterial,
    setCurrentInspectingMaterialsParent,
    setCurrentInspectingMaterialNewLifetime,
    setAllBranchNames,

    // Methods
    stopInspectingMaterial,
    unloadBuildingProject,
    unloadBranchMaterials,
    updateInspectingMaterial,
    increaseLifeTimeUpdater,
    handleUpdateCO2Budget,
    isUpdatingCO2Budget,
    removeDeletedChild,
    reset,
  };
});
